import { ChangeEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import Pill, { WarningIcon } from '../ui/Pill';
import { ErrorNotice, SkeletonRows } from '../ui/States';
import { fmt } from '../../lib/format';
import { errorMessage, label, money } from '../../lib/programs';
import { Audience, audienceName } from '../../lib/events';
import { UPLOAD_ACCEPT, UploadFile, readUpload, stamp } from './externalFiles';
import { th, td, headRow, num, em } from './logistics';
import EventbriteSync from './EventbriteSync';

interface TicketType {
  ticketType: string;
  tickets: number;
  grossCents?: number;
  netCents?: number;
}
interface Duplicate {
  inviteeId: string;
  name: string | null;
  email: string | null;
  audienceKey: string | null;
  status: string;
  tickets: number;
}
interface Summary {
  lastUploadAt: string | null;
  tickets: number;
  cancelled: number;
  orders: number;
  inPerson: number;
  virtual: number;
  byTicketType: TicketType[];
  grossCents?: number | null;
  netCents?: number | null;
  duplicates?: Duplicate[];
}
interface ImportResult {
  dryRun: boolean;
  sheet: string;
  ticketLines: number;
  people: number;
  newPeople: number;
  newContacts: number;
  updated: number;
  unchanged: number;
  declined: number;
  duplicatesWithCore: number;
  restrictedSkipped: number;
  newTicketLines: number;
  changedTicketLines: number;
  noEmail: number;
  noKey: number;
  neverStored: string[];
  unmatchedColumns: string[];
  totals: { tickets: number; cancelled: number; grossCents: number | null; netCents: number | null };
  duplicates: Duplicate[];
}

function Result({ r }: { r: ImportResult }) {
  return (
    <div className="mt-4 border-t border-line pt-3 text-[15px] leading-[22px] text-ink [&>p+p]:mt-1">
      <h4 className="font-semibold mb-1">{r.dryRun ? 'Preview: nothing saved yet' : 'Saved'}</h4>
      <p>
        {fmt(r.ticketLines)} ticket line(s) for {fmt(r.people)} people · {fmt(r.totals.tickets)} active ticket(s)
        {r.totals.cancelled ? `, ${fmt(r.totals.cancelled)} cancelled or refunded` : ''}
        {r.totals.grossCents !== null ? ` · ${money(r.totals.grossCents)} gross` : ''}
        {r.totals.netCents !== null ? `, ${money(r.totals.netCents)} net` : ''}
      </p>
      <p>
        {fmt(r.newPeople)} new on the list ({fmt(r.newContacts)} new contacts, in the public audience) ·{' '}
        {fmt(r.updated)} updated · {fmt(r.unchanged)} unchanged · {fmt(r.declined)} declined (cancelled or refunded)
      </p>
      {r.duplicatesWithCore ? (
        <p>
          {fmt(r.duplicatesWithCore)} already on an invited audience or registered through the core form: they keep
          their audience; check they did not pay twice.
        </p>
      ) : null}
      {r.restrictedSkipped ? (
        <p>{fmt(r.restrictedSkipped)} under-18 committee contact(s) skipped: never added by the platform.</p>
      ) : null}
      {r.noEmail || r.noKey ? (
        <p className="text-ink-2">
          {fmt(r.noEmail)} row(s) without an email and {fmt(r.noKey)} without an order or attendee number were skipped.
        </p>
      ) : null}
      {r.neverStored.length ? <p className="text-ink-2">Never stored: {r.neverStored.join(', ')}.</p> : null}
      {r.unmatchedColumns.length ? (
        <p className="text-ink-2">Columns not used: {r.unmatchedColumns.join(', ')}.</p>
      ) : null}
    </div>
  );
}

export default function EventbritePanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<UploadFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const summary = useQuery<Summary>(
    ['event-eventbrite', eventId],
    async () => (await api.get(`/events/${eventId}/eventbrite`)).data.data,
  );
  const audiences =
    useQuery<{ audiences: Audience[] }>(
      ['event-audiences', eventId],
      async () => (await api.get(`/events/${eventId}/audiences`)).data.data,
    ).data?.audiences ?? [];
  const send = async (confirm: boolean) =>
    (await api.post(`/events/${eventId}/eventbrite`, { fileName: file?.name, content: file?.content, confirm })).data
      .data as ImportResult;
  const preview = useMutation(() => send(false));
  const save = useMutation(() => send(true), {
    onSuccess: () => {
      setFile(null);
      queryClient.invalidateQueries('event-eventbrite');
      queryClient.invalidateQueries('event');
      queryClient.invalidateQueries('event-pheedloop');
    },
  });
  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    setFile(null);
    setFileError(null);
    preview.reset();
    save.reset();
    if (!f) return;
    try {
      setFile(await readUpload(f));
    } catch (err) {
      setFileError((err as Error).message);
    }
  };
  const s = summary.data;
  const dups = s?.duplicates ?? [];
  return (
    <Panel className="mt-14">
      <PanelHeader title="Eventbrite" note={s ? `last upload ${stamp(s.lastUploadAt)}` : undefined} />
      <PanelBody className="text-[15px] leading-[22px]">
        <EventbriteSync eventId={eventId} canEdit={canEdit} />
        {summary.isLoading ? (
          <SkeletonRows rows={2} />
        ) : summary.error ? (
          <ErrorNotice>Could not load the Eventbrite totals.</ErrorNotice>
        ) : s ? (
          <>
            <div className="flex flex-wrap gap-x-12 gap-y-4 mt-2">
              {(
                [
                  [fmt(s.tickets), 'tickets'],
                  [fmt(s.inPerson), 'in person'],
                  [fmt(s.virtual), 'virtual'],
                  ...(s.grossCents !== undefined
                    ? [
                        [
                          money(s.grossCents ?? null),
                          `gross sales${s.netCents !== null && s.netCents !== undefined ? ` · ${money(s.netCents)} net` : ''}`,
                        ],
                      ]
                    : []),
                  ...(s.cancelled ? [[fmt(s.cancelled), 'cancelled or refunded']] : []),
                ] as [string, string][]
              ).map(([v, l]) => (
                <div key={l}>
                  <div className="font-serif text-[32px] leading-9 text-ink tabular">{v}</div>
                  <div className="text-small text-ink-3">{l}</div>
                </div>
              ))}
            </div>
            {s.byTicketType.length ? (
              <table className="w-full mt-6 tabular">
                <thead>
                  <tr className={headRow}>
                    <th className={th}>Ticket type</th>
                    <th className={`${th} ${num}`}>Tickets</th>
                    {s.grossCents !== undefined ? (
                      <>
                        <th className={`${th} ${num}`}>Gross</th>
                        <th className={`${th} ${num}`}>Net</th>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {s.byTicketType.map((t) => (
                    <tr key={t.ticketType} className="border-b border-line">
                      <td className={td}>{t.ticketType}</td>
                      <td className={`${td} ${num}`}>{fmt(t.tickets)}</td>
                      {t.grossCents !== undefined ? (
                        <>
                          <td className={`${td} ${num}`}>{money(t.grossCents)}</td>
                          <td className={`${td} ${num}`}>{money(t.netCents ?? null)}</td>
                        </>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {dups.length ? (
              <div className="mt-8">
                <h3 className="flex gap-[6px] items-center font-semibold text-warning">
                  <WarningIcon />
                  {dups.length} bought a ticket and {dups.length === 1 ? 'was' : 'were'} already invited or registered
                  through the core form
                </h3>
                <p className="text-ink-2 mt-1">Check whether a refund is needed.</p>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full tabular min-w-[560px]">
                    <thead>
                      <tr className={headRow}>
                        <th className={th}>Person</th>
                        <th className={th}>Audience</th>
                        <th className={th}>Status</th>
                        <th className={`${th} ${num}`}>Tickets</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dups.map((d) => (
                        <tr key={d.inviteeId} className="border-b border-line">
                          <td className={td}>
                            {d.name ?? d.email}
                            {d.email && d.name ? <span className={em}>{d.email}</span> : null}
                          </td>
                          <td className={td}>{audienceName(audiences, d.audienceKey)}</td>
                          <td className={td}>
                            <Pill tone="planned">{label(d.status)}</Pill>
                          </td>
                          <td className={`${td} ${num}`}>{fmt(d.tickets)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        {canEdit ? (
          <div className="mt-8 pt-4 border-t border-line">
            <h3 className="font-semibold text-ink">Upload the Eventbrite export</h3>
            <p className="text-ink-2 mt-1 max-w-[72ch]">
              Without the live sync, or to catch up: public tickets are sold through York&rsquo;s Eventbrite account. In
              Eventbrite, open the event, then Reporting → Attendee summary (or Orders) → Export (.xlsx or .csv), and
              upload the file here. Upload again whenever you like: tickets already in are updated, never doubled.
              Cancelled or refunded tickets mark the person declined. Phone numbers, addresses and payment details in
              the file are never kept.
            </p>
            <label className="block mt-3 text-[15px] leading-5 font-semibold text-ink" htmlFor={`eb-file-${eventId}`}>
              Export file (.xlsx or .csv)
            </label>
            <input
              id={`eb-file-${eventId}`}
              type="file"
              accept={UPLOAD_ACCEPT}
              onChange={pick}
              className="mt-[6px] text-[15px] max-w-full"
            />
            {file ? (
              <div className="flex flex-wrap gap-3 mt-3">
                <Button
                  size="sm"
                  variant={preview.data ? 'secondary' : 'primary'}
                  disabled={preview.isLoading}
                  onClick={() => preview.mutate()}
                >
                  {preview.isLoading ? 'Reading…' : 'Preview'}
                </Button>
                <Button
                  size="sm"
                  variant={preview.data ? 'primary' : 'secondary'}
                  disabled={!preview.data || save.isLoading}
                  onClick={() => save.mutate()}
                >
                  {save.isLoading ? 'Saving…' : 'Save to the invitee list'}
                </Button>
                {!preview.data ? <span className="text-small text-ink-3 self-center">Preview first.</span> : null}
              </div>
            ) : null}
            {fileError ? (
              <div className="mt-3">
                <ErrorNotice>{fileError}</ErrorNotice>
              </div>
            ) : null}
            {preview.error || save.error ? (
              <div className="mt-3">
                <ErrorNotice>
                  {errorMessage(preview.error || save.error, 'Could not read the Eventbrite file.')}
                </ErrorNotice>
              </div>
            ) : null}
            {save.data ? <Result r={save.data} /> : preview.data ? <Result r={preview.data} /> : null}
          </div>
        ) : (
          <p className="text-ink-2 mt-6 max-w-[72ch]">
            Public tickets are sold through York&rsquo;s Eventbrite account; the totals above come from the live sync or
            the last export uploaded. Phone numbers, addresses and payment details are never kept.
          </p>
        )}
      </PanelBody>
    </Panel>
  );
}
